import React, { useEffect, useMemo, useState } from "react";

const SectionLinkRow = ({ link, section, onUpdate, onDelete }) => {
  const [order, setOrder] = useState(link.order ?? 0);
  useEffect(() => setOrder(link.order ?? 0), [link.order]);
  return <div className="pb-member-row">
    <span>{section?.title || `Section ${link.section}`}</span>
    <label>Order<input className="pb-input" type="number" min="0" value={order} onChange={(e) => setOrder(e.target.value)} /></label>
    <button className="pb-action-btn" type="button" onClick={() => onUpdate(link.id, { order: Number(order) || 0 })}>Save order</button>
    <button className="pb-action-btn is-danger" type="button" onClick={() => window.confirm("Remove this section from the page?") && onDelete(link.id)}>Remove</button>
  </div>;
};

const ExistingPagesList = ({ pages, sections, pageSections, setMessage, onUpdate, onDelete, onAddSection, onUpdateSection, onDeleteSection }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ title: "", description: "" });
  const [newSectionId, setNewSectionId] = useState("");
  const [newOrder, setNewOrder] = useState(0);
  const [publishingId, setPublishingId] = useState(null);
  const [publishFeedback, setPublishFeedback] = useState(null);

  const sectionById = useMemo(() => new Map(sections.map((row) => [String(row.id), row])), [sections]);
  const links = pageSections.filter((link) => String(link.page) === String(editingId));
  const linkedIds = new Set(links.map((link) => String(link.section)));
  const availableSections = sections.filter((section) => !linkedIds.has(String(section.id)));

  const beginEdit = (page) => {
    setEditingId(page.id);
    setDraft({ title: page.title || "", description: page.description || "" });
    setNewSectionId("");
    setNewOrder(0);
  };

  const save = async (pageId) => {
    if (!draft.title.trim()) return setMessage("Page title is required.");
    if (await onUpdate(pageId, { title: draft.title.trim(), description: draft.description.trim() })) setEditingId(null);
  };

  const addSection = async () => {
    if (!newSectionId) return setMessage("Choose a section to add.");
    if (await onAddSection({ page: editingId, section: Number(newSectionId), order: Number(newOrder) || 0 })) {
      setNewSectionId("");
      setNewOrder(0);
    }
  };

  const togglePublish = async (page) => {
    setPublishingId(page.id);
    setPublishFeedback(null);
    const nextPublished = !page.is_published;
    const saved = await onUpdate(page.id, { is_published: nextPublished });
    setPublishingId(null);
    if (saved) {
      setPublishFeedback({ id: page.id, published: nextPublished });
      window.setTimeout(() => setPublishFeedback((current) => current?.id === page.id ? null : current), 2200);
    }
  };

  return <div className="pb-card pb-list-card pb-list-card--pages"><div className="pb-list-card-heading"><div><span className="pb-list-card-kicker">Pages</span><h2>Existing Pages</h2></div><span className="pb-count-badge">{pages.length}</span></div><div className="pb-table-wrap"><table className="pb-table pb-table--pages">
    <thead><tr><th>Page</th><th>Description</th><th>Status</th><th>Link</th><th>Actions</th></tr></thead>
    <tbody>
      {pages.map((page, index) => {
        const editing = editingId === page.id;
        const path = `/p/${page.id}`;
        return <React.Fragment key={page.id}>
          <tr className={`pb-data-row ${index % 2 ? "is-alt" : ""}`}>
            <td className="pb-table-title">{page.title}</td>
            <td className="pb-table-description">{page.description || "—"}</td>
            <td>{page.is_published ? <span className="pb-badge is-published">Published</span> : <span className="pb-badge">Draft</span>}{publishFeedback?.id === page.id && <small className="pb-table-feedback" role="status">{publishFeedback.published ? "Added to I/O Pages" : "Removed from I/O Pages"}</small>}</td>
            <td><div className="pb-table-link"><a className="pb-page-link" href={path} target="_blank" rel="noreferrer">{path}</a><button className="pb-copy-btn" type="button" onClick={() => navigator.clipboard.writeText(`${window.location.origin}${path}`)}>Copy</button></div></td>
            <td><div className="pb-table-actions"><button className={`pb-action-btn pb-publish-btn ${page.is_published ? "is-published" : ""}`} type="button" aria-pressed={!!page.is_published} disabled={publishingId === page.id} onClick={() => togglePublish(page)}>{publishingId === page.id ? (page.is_published ? "Unpublishing…" : "Publishing…") : (page.is_published ? "Unpublish" : "Publish")}</button><button className="pb-action-btn is-secondary" type="button" onClick={() => editing ? setEditingId(null) : beginEdit(page)}>{editing ? "Close" : "Edit"}</button><button className="pb-action-btn is-danger" type="button" onClick={() => window.confirm(`Delete page “${page.title}”?`) && onDelete(page.id)}>Delete</button></div></td>
          </tr>
          {editing && <tr className="pb-edit-row"><td colSpan="5"><div className="pb-edit-panel">
            <div className="pb-edit-fields"><input className="pb-input" value={draft.title} onChange={(e) => setDraft((v) => ({ ...v, title: e.target.value }))} aria-label="Page title" /><textarea className="pb-textarea" value={draft.description} onChange={(e) => setDraft((v) => ({ ...v, description: e.target.value }))} aria-label="Page description" /></div>
            <h3 className="pb-member-heading">Sections in this page</h3>
            {links.map((link) => <SectionLinkRow key={link.id} link={link} section={sectionById.get(String(link.section))} onUpdate={onUpdateSection} onDelete={onDeleteSection} />)}
            {links.length === 0 && <div className="pb-muted">No sections in this page.</div>}
            <div className="pb-add-member"><select className="pb-select" value={newSectionId} onChange={(e) => setNewSectionId(e.target.value)}><option value="">Add a section…</option>{availableSections.map((section) => <option key={section.id} value={section.id}>{section.title}</option>)}</select><input className="pb-input" type="number" min="0" value={newOrder} onChange={(e) => setNewOrder(e.target.value)} aria-label="New section order" /><button className="pb-action-btn" type="button" onClick={addSection}>Add section</button></div>
            <div className="pb-item-actions"><button className="pb-action-btn" type="button" onClick={() => save(page.id)}>Save page</button><button className="pb-action-btn is-secondary" type="button" onClick={() => setEditingId(null)}>Close</button></div>
          </div></td></tr>}
        </React.Fragment>;
      })}
      {pages.length === 0 && <tr><td colSpan="5" className="pb-muted">No pages yet.</td></tr>}
    </tbody>
  </table></div></div>;
};

export default ExistingPagesList;
